/**
 * ThemeManager - Handles theme switching between light, dark, and system preferences
 */
class ThemeManager {
  constructor() {
    this.theme = this.getStoredTheme();
    this.init();
  }

  /**
   * Gets the stored theme preference with error handling for private browsing
   * @returns {string} The stored theme ('light', 'dark', or 'system')
   */
  getStoredTheme() {
    try {
      return localStorage.getItem('theme') || 'system';
    } catch {
      console.warn('localStorage unavailable, using system theme');
      return 'system';
    }
  }

  /**
   * Initializes the theme manager and sets up system preference listener
   */
  init() {
    // Listen for system theme changes
    window.matchMedia('(prefers-color-scheme: dark)')
      .addEventListener('change', (e) => {
        if (this.theme === 'system') {
          this.applyTheme(e.matches ? 'dark' : 'light');
        }
      });

    // Update UI to reflect current theme
    this.updateUI();
  }

  /**
   * Sets a new theme and persists it to localStorage
   * @param {string} newTheme - The theme to set ('light', 'dark', or 'system')
   */
  setTheme(newTheme) {
    this.theme = newTheme;

    try {
      localStorage.setItem('theme', newTheme);
    } catch {
      console.warn('Unable to save theme preference');
    }

    // Determine effective theme
    let effectiveTheme = newTheme;
    if (newTheme === 'system') {
      effectiveTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
    }

    this.applyTheme(effectiveTheme);
    this.updateUI();
  }

  /**
   * Applies the theme to the document
   * @param {string} theme - The theme to apply ('light' or 'dark')
   */
  applyTheme(theme) {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }

  /**
   * Updates the UI to reflect the current theme selection
   */
  updateUI() {
    const activeClasses = ['bg-neutral-800', 'text-white'];
    const inactiveClasses = ['text-neutral-300', 'hover:text-white'];

    document.querySelectorAll('[data-theme-toggle]').forEach(button => {
      const buttonTheme = button.getAttribute('data-theme-toggle');
      const isActive = buttonTheme === this.theme;

      if (isActive) {
        button.classList.add(...activeClasses);
        button.classList.remove(...inactiveClasses);
      } else {
        button.classList.remove(...activeClasses);
        button.classList.add(...inactiveClasses);
      }
    });
  }

  /**
   * Gets the current theme setting
   * @returns {string} The current theme
   */
  getTheme() {
    return this.theme;
  }
}

const themeManager = new ThemeManager();

// Attach click handlers to theme toggle buttons
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-theme-toggle]').forEach(button => {
    button.addEventListener('click', () => {
      const theme = button.getAttribute('data-theme-toggle');
      themeManager.setTheme(theme);
    });
  });
});
